import { find } from 'lodash';
import { PRIMARY_INDUSTRIES } from '@/constants/industries';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';

type SortBy =
  | 'market-cap-large'
  | 'market-cap-small'
  | 'top-gainers'
  | 'biggest-losers'
  | 'growth'
  | 'dividend-yield-high';

type SortByRule =
  | '["order_by","market_cap","desc"]'
  | '["order_by","market_cap","asc"]'
  | '["order_by","market_cap_movement_1d","desc"]'
  | '["order_by","market_cap_movement_1d","asc"]'
  | '["order_by","future_three_year_growth","desc"]'
  | '["order_by","current_dividend","desc"]';

export const sortByRuleMap: Record<SortBy, SortByRule> = {
  'market-cap-large': '["order_by","market_cap","desc"]',
  'market-cap-small': '["order_by","market_cap","asc"]',
  'top-gainers': '["order_by","market_cap_movement_1d","desc"]',
  'biggest-losers': '["order_by","market_cap_movement_1d","asc"]',
  growth: '["order_by","future_three_year_growth","desc"]',
  'dividend-yield-high': '["order_by","current_dividend","desc"]',
};

const getSortByRule = (sortBy: SortBy): SortByRule | undefined =>
  sortByRuleMap[sortBy];

const getOTCPKRule = (market: PrimaryCountryISOElseGlobal) => {
  return market && market === 'us'
    ? `,["aor",[["exchange_symbol","not_in",["otcpk"]]]]`
    : '';
};

const getMarketRule = (market) =>
  market && market !== 'any' && market !== 'global'
    ? `,["aor",[["country_name","in",["${market}"]]]]`
    : '';

const getDividendRule = (sortBy?: SortBy) => {
  if (sortBy !== 'dividend-yield-high') return '';
  return `,["current_dividend","between",0,20]`;
};

export function getStocksRules(
  market: PrimaryCountryISOElseGlobal,
  industry: string | 'any',
  sortBy?: SortBy
) {
  const sortByRule = getSortByRule(sortBy || 'market-cap-large');
  const OTCPKRule = getOTCPKRule(market);
  const marketRule = getMarketRule(market);
  const dividendRule = getDividendRule(sortBy || 'market-cap-large');
  let industryRule = '';
  if (typeof industry !== 'undefined') {
    let industryKey = '';
    const industryItem = find(PRIMARY_INDUSTRIES, (value, key) => {
      if (value.slug === industry) {
        industryKey = key;
      }
      return value.slug === industry;
    });
    industryRule =
      typeof industryItem !== 'undefined' && industryKey !== ''
        ? `,["aor",[["primary_industry_id","in",["${industryKey}"]]]]`
        : '';
  }
  return `[${sortByRule},["primary_flag","=",true],["grid_visible_flag","=",true],["market_cap","is_not_null"],["is_fund","=",false]${dividendRule}${marketRule}${industryRule}${OTCPKRule}]`;
}

export const useStocksRules = (
  market: PrimaryCountryISOElseGlobal,
  industry: string | 'any',
  sortBy?: SortBy
) => {
  return getStocksRules(market, industry, sortBy);
};
